import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
    Backdrop,
    SpeedDial,
    SpeedDialIcon,
    SpeedDialAction,
    useMediaQuery,
    Tooltip,
    CircularProgress,
    Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import PromptService from '../../../../../services/api/prompts';
import { continueJourneyStatusCodes, formatDate } from '../utils';
import { useAuth } from "../../../../../services/auth/AuthContext";
import { CUSTOMER_FORM_URL } from '../../../../../config';
import { HighlightOff } from '@mui/icons-material';
import { red } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { OpenInNew } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const promptTableColumns = [
    {
        label: 'Disposition',
        getValue: (detail) => detail.disposition,
    },
    {
        label: 'Mode',
        getValue: (detail) => detail.type,
    },
    {
        label: 'Notes',
        getValue: (detail) => detail.comments,
    },
    {
        label: 'By',
        getValue: (detail) => `${detail.created_by.first_name} ${detail.created_by.last_name}`,
    },
    {
        label: 'Time',
        getValue: (detail) => formatDate(detail.created_at),
    },
];

export const CallDetailDrawer = ({
    open,
    handleClose,
    initialData,
    priority,
    setPriority,
    rejectApplication,
}) => {
    const { user } = useAuth();
    const userRoles = user.role;
    const isSupport = userRoles.includes('support');
    const isPartner = userRoles.includes('partner');
    const isConnector = userRoles.includes('connector');
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // State for call details, dialog visibility, new row data
    const [callDetails, setCallDetails] = useState([]); // Using dummy data
    const [rejectReasons, setRejectReasons] = useState([]);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonNotes, setRejectReasonNotes] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [newCallDisposition, setNewCallDisposition] = useState('');
    const [newNotes, setNewNotes] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openReminderDialog, setOpenReminderDialog] = useState(false);
    const [prioritySelect, setPrioritySelect] = useState(priority);
    const [openPriorityDialog, setOpenPriorityDialog] = useState(false);
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [confirmRejectAnchorEl, setConfirmRejectAnchorEl] = useState(null);

    const [dispositions, setDispositions] = useState([]);
    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const handleSpeedDialOpen = () => setSpeedDialOpen(true);
    const handleSpeedDialClose = () => setSpeedDialOpen(false);

    const action = {
        'none': {
            'icon': <PriorityHighIcon />,
            'label': 'None'
        },
        'low': {
            'icon': <LowPriorityIcon />,
            'color': 'grey',
            'label': 'Low'
        },
        'medium': {
            'icon': <KeyboardArrowUpIcon />,
            'color': 'orange',
            'label': 'Medium'
        },
        'high': {
            'icon': <KeyboardDoubleArrowUpIcon />,
            'color': 'green',
            'label': 'High'
        },
        'not_interested': {
            'icon': <NotInterestedIcon />,
            'color': 'red',
            'label': 'Not Interested'
        }
    }

    const applicationSubmissionStatus = initialData.enquiry_details.application_submission_status;
    const isCancelledOrUnserviceable = ['cancelled', 'unserviceable'].includes(applicationSubmissionStatus);
    const isApproved = applicationSubmissionStatus === 'approved';
    const isRejected = applicationSubmissionStatus === 'rejected';
    const isPushedToField = applicationSubmissionStatus === 'pushed_to_field';

    // Fetch call details on component mount
    useEffect(() => {
        if (open) {
            PromptService.getPrompts(initialData.enquiry_details.id)
                .then(response => setCallDetails(response.data))
                .catch(error => console.error('Error fetching call details:', error));

            PromptService.getDispositions()
                .then(response => setDispositions(response.data))
                .catch(error => console.error('Error fetching dispositions:', error));
        }
    }, [initialData.enquiry_details.id, open]);

    useEffect(() => {
        if (open && !(isApproved || isCancelledOrUnserviceable || isRejected)) {
            PromptService.getRejectReasons()
                .then(response => setRejectReasons(response.data))
                .catch(error => console.error('Error fetching rejection reasons:', error));
        }
    }, [isApproved, isCancelledOrUnserviceable, isRejected, open]);

    // Handler to open the dialog for adding new call details
    const handleAddPromptClick = () => {
        setOpenDialog(true);
    };

    const handleSendReminderClick = () => {
        setOpenReminderDialog(true);
    };

    const handleSetPriorityClick = () => {
        setOpenPriorityDialog(true);
    };

    const handleRejectClick = () => {
        setOpenRejectDialog(true);
    };

    const actions = (!isCancelledOrUnserviceable && !isRejected) ? [
        {
            icon: <AddIcon />,
            name: 'Add Prompt',
            onClick: handleAddPromptClick,
        },
        (isSupport && {
            icon: <SendIcon />,
            name: 'Send Reminder',
            onClick: handleSendReminderClick,
        }),
        (
            (isSupport || isPartner || isConnector) &&
            continueJourneyStatusCodes.includes(initialData.current_status_code) && {
                icon: <EditNoteIcon />,
                name: 'Continue Journey',
                onClick: () => window.open(`${CUSTOMER_FORM_URL}/verification/${initialData.enquiry_details.flow_token}`),
            }
        ),
        (
            (isSupport || isConnector) && {
                icon: action[priority].icon,
                name: 'Set Priority',
                onClick: handleSetPriorityClick,
                color: action[priority].color
            }
        ),
        (
            isSupport &&
            !isApproved &&
            !isPushedToField && {
                icon: <HighlightOff />,
                name: 'Reject Application',
                onClick: handleRejectClick,
                color: red['500'],
            }
        ),
    ].filter(Boolean) : [];

    // Handler to close the dialog without adding
    const handleDialogClose = () => {
        setOpenReminderDialog(false);
        setOpenDialog(false);
        setOpenPriorityDialog(false);
        setOpenRejectDialog(false);
        setNewCallDisposition('');
        setNewNotes('');
    };

    // Handler to add the new call details
    const handleAddNewRow = () => {
        const newRow = {
            disposition: newCallDisposition,
            comments: newNotes,
            enquiry: initialData.enquiry_details.id,
            type: 'call'
        };

        PromptService.createPrompt(newRow)
            .then((response) => {
                setCallDetails([response, ...callDetails]);
            })
        handleDialogClose();

    };

    const handleConfirmAction = () => {
        PromptService.sendReminder({ "enquiry_id": initialData.enquiry_details.id, "note": newNotes })
            .then((response) => {
                setCallDetails([response, ...callDetails]);
            })
        setOpenReminderDialog(false);
    }

    const handleSetPrioritySave = () => {
        PromptService.setPriority({ "enquiry_id": initialData.enquiry_details.id, "priority": prioritySelect })
            .then((response) => {
                setPriority(prioritySelect);
            })
        setOpenPriorityDialog(false);
    }

    const handleRejectEnquiry = () => {
        const enquiryId = initialData.enquiry_details.id;
        const payload = {
            "enquiry_id": enquiryId,
            "rejection_id": rejectReason,
            "rejection_note": rejectReasonNotes,
        };

        // Make API call
        PromptService.rejectEnquiry(payload).then((response) => {
            rejectApplication(enquiryId);

            const selectedRejectReason = rejectReasons.find(reason => reason.id === rejectReason);

            initialData.enquiry_details.application_submission_status = 'rejected';
            initialData.rejection_reason = {
                rejection_category: selectedRejectReason.rejection_category,
                rejection_reason: selectedRejectReason.rejection_reason
            };
            initialData.enquiry_details.rejection_note = rejectReasonNotes;

            setConfirmRejectAnchorEl(null);
            setOpenRejectDialog(false);

            setIsRejecting(true);

            enqueueSnackbar('Application rejected successfully!', {
                variant: 'success',
                autoHideDuration: 5000,
                action: (snackbarKey) => (
                    <Button
                        component={Link}
                        to={`/search?search_by=enquiry_id&search_term=${enquiryId}`}
                        target="_blank"
                        onClick={() => closeSnackbar(snackbarKey)}
                        endIcon={<OpenInNew />}
                        sx={{
                            color: 'inherit',
                            '&:hover': {
                                backgroundColor: '#f5f5f522',
                                fontWeight: 'normal',
                                color: 'inherit',
                            },
                        }}
                    >
                        View
                    </Button>
                ),
            });
        }).catch((error) => {
            console.error('Error rejecting enquiry:', error);
            enqueueSnackbar('Error rejecting enquiry. Please try again.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
        })
        .finally(() => {
            setIsRejecting(false);
        });
    };

    // Adding this disposition is what pushes a lead to field, and nobody
    // should do that from the dashboard any more. The value stays valid in the
    // API; it is only removed from the dropdown.
    const HIDDEN_DISPOSITIONS = ['Pushed To Field'];
    const selectableDispositions = dispositions.filter(
        ({ value, label }) => !HIDDEN_DISPOSITIONS.includes(value ?? label)
    );

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            {isMobile ? (
                <>
                    <Backdrop
                        open={speedDialOpen}
                        sx={({ zIndex }) => ({
                            zIndex: zIndex.fab,
                        })}
                    />
                    <SpeedDial
                        ariaLabel="SpeedDial"
                        open={speedDialOpen}
                        onOpen={handleSpeedDialOpen}
                        onClose={handleSpeedDialClose}
                        icon={<SpeedDialIcon />}
                        sx={{ position: 'absolute', bottom: 16, right: 16 }}
                    >
                        {actions.map((action) => (
                            <SpeedDialAction
                                key={action.name}
                                icon={action.icon}
                                tooltipTitle={action.name}
                                tooltipOpen
                                onClick={action.onClick}
                            />
                        ))}
                    </SpeedDial>
                </>
            ) : (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', marginBottom: 2 }}>
                    {actions.map((action) => (
                        <Tooltip
                            key={action.name}
                            title={action.name}
                        >
                            <IconButton
                                color="primary"
                                onClick={action.onClick}
                                sx={{ color: action.color }}
                            >
                                {action.icon}
                            </IconButton>
                        </Tooltip>
                    ))}
                </Box>
            )}

            {
                isRejected && initialData.rejection_reason && (
                    <TableContainer
                        component={Paper}
                        sx={{ border: '1px solid #ddd', marginBottom: '10px' }}
                    >
                        <Table stickyHeader aria-label="call details table" sx={{ tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        key={'rcategory'}
                                        sx={{
                                            border: '1px solid #ddd',
                                            fontWeight: 'bold',
                                            padding: '8px',
                                        }}
                                    >
                                        Rejection Category
                                    </TableCell>
                                    <TableCell
                                        key={'rreason'}
                                        sx={{
                                            border: '1px solid #ddd',
                                            fontWeight: 'bold',
                                            padding: '8px',
                                        }}
                                    >
                                        Rejection Reason
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                    <TableCell
                                        sx={{
                                            border: '1px solid #ddd',
                                            padding: '8px',
                                        }}
                                    >
                                        {initialData.rejection_reason.rejection_category}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            border: '1px solid #ddd',
                                            padding: '8px',
                                        }}
                                    >
                                        {initialData.rejection_reason.rejection_reason}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        <Table stickyHeader aria-label="call details table" sx={{ tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow>

                                    <TableCell
                                        key={'rreason'}
                                        sx={{
                                            border: '1px solid #ddd',
                                            fontWeight: 'bold',
                                            padding: '8px',
                                        }}
                                    >
                                        Rejection Note
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>

                                    <TableCell
                                        sx={{
                                            border: '1px solid #ddd',
                                            padding: '8px',
                                        }}
                                    >
                                        {initialData.enquiry_details.rejection_note}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            }

            {/* Render the existing call details in a table with borders */}
            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100% - 100px)', overflowY: 'auto', border: '1px solid #ddd' }}>
                <Table stickyHeader aria-label="call details table" sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                        <TableRow>
                            {promptTableColumns.map(({ label }) => (
                                <TableCell
                                    key={label}
                                    sx={{
                                        border: '1px solid #ddd',
                                        fontWeight: 'bold',
                                        padding: '8px',
                                    }}
                                >
                                    {label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {callDetails.map((detail, index) => (
                            <TableRow key={detail.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                {promptTableColumns.map(({ getValue }) => (
                                    <TableCell
                                        sx={{
                                            border: '1px solid #ddd',
                                            padding: '8px',
                                        }}
                                    >
                                        {getValue(detail)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog to add a new row */}
            <Dialog open={openDialog} onClose={handleDialogClose} sx={{ '& .MuiDialog-paper': { width: '100%' } }}>
                <DialogTitle>Add Call Details</DialogTitle>
                <DialogContent>
                    {/* Disposition Dropdown */}
                    <FormControl fullWidth size="small" sx={{ marginBottom: 1 }}>
                        <InputLabel>Disposition</InputLabel>
                        <Select
                            value={newCallDisposition}
                            onChange={(e) => setNewCallDisposition(e.target.value)}
                            label="Disposition"
                        >
                            {selectableDispositions.map((disposition, index) => (
                                <MenuItem key={index} value={disposition.value}>{disposition.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Notes"
                        fullWidth
                        size="small"
                        value={newNotes}
                        onChange={(e) => setNewNotes(e.target.value)}
                        sx={{ marginBottom: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: 1, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button onClick={handleDialogClose} color="secondary" size="small" sx={{ marginRight: 1 }}>Cancel</Button>
                        <Button onClick={handleAddNewRow} color="primary" size="small">Add</Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openPriorityDialog}
                onClose={handleDialogClose}
                sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: '300px' } }}
            >
                <DialogTitle>Set Priority</DialogTitle>
                <DialogContent>
                    {/* Priority Dropdown */}
                    <FormControl fullWidth size="small" sx={{ marginBottom: 2 }}>
                        <Select
                            value={prioritySelect}
                            onChange={(e) => setPrioritySelect(e.target.value)}
                            label="Priority"
                        >
                            {Object.entries(action).map(([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <Typography>{value.label}</Typography>
                                        <Box>
                                            {value.icon}
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ padding: 1, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            onClick={handleDialogClose}
                            color="secondary"
                            size="small"
                            sx={{ marginRight: 1 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSetPrioritySave}
                            color="primary"
                            size="small"
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog
                component={'form'}
                onSubmit={(e) => {
                    e.preventDefault();
                }}
                open={openRejectDialog}
                onClose={handleDialogClose}
                sx={{
                    '& .MuiDialog-paper': {
                        width: '100%',
                        maxWidth: '600px'
                    }
                }}
            >
                <DialogTitle>Reject Application</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <FormControl
                        size="small"
                        required
                        fullWidth
                        sx={{ my: 1 }}
                    >
                        <InputLabel id="reject-reason-label">
                            Rejection Reason
                        </InputLabel>
                        <Select
                            label="Rejection Reason"
                            labelId="reject-reason-label"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            required
                        >
                            {rejectReasons.map(({ id, rejection_category, rejection_reason, rejection_description }) => (
                                <MenuItem key={id} value={id}>
                                    <Box px={2}>
                                        <Typography fontWeight={'bold'}>
                                            {rejection_category} - {rejection_reason}
                                        </Typography>
                                        <Typography
                                            fontSize={'small'}
                                            color="textSecondary"
                                            sx={{ textWrap: 'wrap' }}
                                        >
                                            {rejection_description}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Description"
                        size="small"
                        value={rejectReasonNotes}
                        onChange={(e) => setRejectReasonNotes(e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                    />
                </DialogContent>

                <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            onClick={handleDialogClose}
                            color="secondary"
                            size="small"
                            sx={{ marginRight: 1 }}
                            disabled={isRejecting}
                        >
                            Cancel
                        </Button>
                        <Button
                            aria-describedby="confirm-reject-popper"
                            color="primary"
                            size="small"
                            type="submit"
                            onClick={(e) => {
                                if (
                                    e.target instanceof HTMLButtonElement &&
                                    e.target.form.checkValidity()
                                ) {
                                    setConfirmRejectAnchorEl(e.currentTarget)
                                }
                            }}
                            disabled={isRejecting}
                            startIcon={isRejecting && (
                                <CircularProgress
                                    size={'1rem'}
                                    color="inherit"
                                />
                            )}
                        >
                            Save
                        </Button>

                        <Popover
                            id="confirm-reject-popper"
                            open={!!confirmRejectAnchorEl}
                            anchorEl={confirmRejectAnchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }}>
                                Do you really want to reject this application?
                                <Box
                                    display={'flex'}
                                    justifyContent={'end'}
                                    alignItems={'center'}
                                    mt={2}
                                    gap={1}
                                >
                                    <Button
                                        color="secondary"
                                        size="small"
                                        onClick={() => {
                                            setConfirmRejectAnchorEl(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        color="primary"
                                        size="small"
                                        type="submit"
                                        onClick={() => {
                                            setConfirmRejectAnchorEl(null);
                                            handleRejectEnquiry();
                                        }}
                                    >
                                        Confirm
                                    </Button>
                                </Box>
                            </Box>
                        </Popover>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog open={openReminderDialog} onClose={handleDialogClose} sx={{ '& .MuiDialog-paper': { width: '100%' } }}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <Box sx={{ marginBottom: 2 }}>
                        <Typography variant="body1">
                            Are you sure you want to proceed with this action?
                        </Typography>
                    </Box>
                    <TextField
                        label="Notes"
                        fullWidth
                        size="small"
                        value={newNotes}
                        onChange={(e) => setNewNotes(e.target.value)}
                        sx={{ marginBottom: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: 1, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button onClick={handleDialogClose} color="secondary" size="small" sx={{ marginRight: 1 }}>Cancel</Button>
                        <Button onClick={handleConfirmAction} color="primary" size="small">Confirm</Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
